import React, { useState, useEffect, useRef } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Actions
import { fetchOrders, fetchFiltersLabels, orderBulkActions } from '~/redux/actions/orders-actions';
import { CLEAR_ORDERS_FILTERS } from '~/redux/actions/action-types';
import { failure, success } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { debounce } from '~/utils/helperFunctions';

// Component
import Table from '~/components/Orders/OrderListing';
import AdvanceFilters from './AdvanceFilters';
import NoOrdersScreen from './NoOrderScreen';
import CreateTestOrderConfirmModal from './CreateTestOrderConfirmModal';
import RescheduleOrderModal from '~/components/AdminPortal/Orders/RescheduleOrderModal';
import ConfirmModal from '../General/ConfirmModal';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcButton from '~/components/General/OlcButton';
import OlcSearchInput from '~/components/General/OlcSearchInput';

// MUI Components
import { Container, Box, Grid, Typography, useMediaQuery, MenuItem } from '@mui/material';

// Icons
import {
  TemplateAddCIcon,
  ProcessOrderCIcon,
  CancelOrderCIcon,
  ExportModalCIcon,
  ExportNewCIcon,
  CancelOrderNewCIcon,
  PayAndProcessNewCIcon,
  ProcessOrderNew,
  CancelOrderModalCIcon,
  ExportModalNewCIcon,
} from '~/assets/images';
import DoneIcon from '@mui/icons-material/Done';

// styles
import './styles.scss';

const Orders = () => {
  // Use the useMediaQuery hook to check the screen size
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  // Determine the maxWidth based on the screen size
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const [openCreateConfirm, setOpenCreateConfirm] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState(false);
  const [openAdvanceFilters, setOpenAdvanceFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [actionModal, setActionModal] = useState(false);
  const [action, setAction] = useState({
    message: '',
    currentAction: null,
    successButtonName: '',
    loading: false,
  });
  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const childRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentMode = useSelector((state) => state.userReducers.user.isLiveMode || false);

  const handleCreateOrder = () => {
    if (currentMode === true) {
      navigate('/orders/create-order');
    } else {
      setOpenCreateConfirm(true);
    }
  };

  const confirmCreateOrder = () => {
    setOpenCreateConfirm(false);
    navigate('/orders/create-order');
  };

  const totalOrdersInDB = useSelector((state) => state.ordersReducers.totalOrdersInDB);

  const applyFilters = useSelector((state) => state.ordersReducers.applyFilters);
  const filters = useSelector((state) => state.ordersReducers.filters);
  const filterSize = useSelector((state) => state.ordersReducers.filterSize);

  const handleSearch = () => {
    if (search.trimStart()) {
      setPagination({ ...pagination, page: 0 });
      setSearchApplied(true);
      getOrders();
    }
  };

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const debouncedHandleKeyDown = debounce(searchKeyDown, 300);

  const getOrders = () => {
    let payload = {
      pagination,
      search: search.length ? search.trimStart() : null,
      applyFilters: applyFilters,
      filters: applyFilters ? filters : null,
    };
    dispatch(fetchOrders(payload));
  };

  const cancelOrders = async () => {
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    try {
      setAction({ ...action, loading: true });
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'cancel',
        search: search.length ? search : null,
        applyFilters: applyFilters,
        filters: applyFilters ? filters : null,
      };
      const response = await dispatch(orderBulkActions(payload));
      if (response?.status === 200) {
        getOrders();
        setSelectedRows([]);
        dispatch(success(response.data?.message));
      } else {
        dispatch(failure(response?.data?.message || MESSAGES.GENERAL_ERROR));
      }
      setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
      }, 2000);
    }
  };

  const payAndProcessOrders = async () => {
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    try {
      setAction({ ...action, loading: true });
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'pay and process',
        search: search.length ? search : null,
        applyFilters: applyFilters,
        filters: applyFilters ? filters : null,
      };
      const response = await dispatch(orderBulkActions(payload));
      if (response?.status === 200) {
        getOrders();
        setSelectedRows([]);
        dispatch(success(response.data?.message));
      } else {
        dispatch(failure(response?.data?.message || MESSAGES.GENERAL_ERROR));
      }
      setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
      }, 1000);
    }
  };

  const exportOrders = async () => {
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    try {
      setAction({ ...action, loading: true });
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'export',
        search: search.length ? search : null,
        applyFilters: applyFilters,
        filters: applyFilters ? filters : null,
      };
      const response = await dispatch(orderBulkActions(payload));
      if (response?.status === 200) {
        getOrders();
        setSelectedRows([]);
        dispatch(success(response.data?.message));
      } else {
        dispatch(failure(response?.data?.message || MESSAGES.GENERAL_ERROR));
      }
      setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
      }, 2000);
    }
  };

  selectedRows[0] === -1 || selectedRows.length > 1
    ? MESSAGES.CONTACTS.MULTI_DELETE_HEADER
    : MESSAGES.CONTACTS.SINGLE_DELETE_HEADER;

  const handleActions = async (type) => {
    if (type === 'cancel') {
      setOpenConfirmModal({
        type: 'cancel',
        open: true,
        message:
          selectedRows[0] === -1 || selectedRows.length > 1
            ? MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.MULTI_MESSAGE
            : MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.MESSAGE,
        subMessage: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.MULTI_SUB_MESSAGE,
        handleOk: cancelOrders,
        header: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.HEADING,
        successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.SUCCESS_BUTTON,
        icon: CancelOrderModalCIcon,
        handleClose: () => setOpenConfirmModal((prev) => ({ ...prev, open: false })),
      });
    } else if (type === 'payAndProcess') {
      setOpenConfirmModal({
        type: 'payAndProcess',
        open: true,
        message: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.MESSAGE,
        subMessage: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.MULTI_SUB_MESSAGE,
        handleOk: payAndProcessOrders,
        header: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.HEADING,
        successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.SUCCESS_BUTTON,
        icon: ProcessOrderNew,
        className: 'payAndProcessModal',
        handleClose: () => setOpenConfirmModal((prev) => ({ ...prev, open: false })),
      });
    } else if (type === 'export') {
      setOpenConfirmModal({
        type: 'export',
        open: true,
        message:
          selectedRows[0] === -1 || selectedRows.length > 1
            ? MESSAGES.ORDERS.DETAIL.MODALS.EXPORT.MULTI_MESSAGE
            : MESSAGES.ORDERS.DETAIL.MODALS.EXPORT.MESSAGE,
        handleOk: exportOrders,
        header: MESSAGES.ORDERS.DETAIL.MODALS.EXPORT.HEADING,
        successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.EXPORT.SUCCESS_BUTTON,
        icon: ExportModalNewCIcon,
        className: 'exportModalComponent',
        isExport: true,
        handleClose: () => setOpenConfirmModal((prev) => ({ ...prev, open: false })),
      });
    }
    setActionModal(true);
  };

  useEffect(() => {
    setIsMounted(true);
    getOrders();
    dispatch(fetchFiltersLabels());
    return () => {
      dispatch({ type: CLEAR_ORDERS_FILTERS });
    };
  }, []);

  useEffect(() => {
    if (isMounted) {
      getOrders();
    }
  }, [pagination.page, pagination.pageSize, filterSize, filters]);

  useEffect(() => {
    if (!search && isMounted) {
      setSearchApplied(false);
      setSearch('');
      getOrders();
    }
  }, [search]);

  useEffect(() => {
    if (isMounted) {
      let payload = {
        DEFAULT_PAGINATION,
        search: search.length ? search : null,
      };
      dispatch(fetchOrders(payload));
    }

    if (openCreateConfirm === true && currentMode === true) {
      setOpenCreateConfirm(false);
      navigate('/orders/create-order');
    }
  }, [currentMode]);

  const noOrdersInDb = () => {
    return (
      <Container maxWidth={maxWidth} className="olcEmptyStatePageContainer">
        <NoOrdersScreen
          handleCreateOrder={handleCreateOrder}
          openCreateConfirm={openCreateConfirm}
          setOpenCreateConfirm={setOpenCreateConfirm}
          confirmCreateOrder={confirmCreateOrder}
        />
      </Container>
    );
  };

  if (totalOrdersInDB < 1 && totalOrdersInDB !== null) {
    return noOrdersInDb();
  }

  return (
    <Container maxWidth={maxWidth} className="ordersPageContainer olcPrimaryListPage">
      <Grid container columnSpacing={1} className="ordersToolbarRow">
        <Grid
          item
          lg={8}
          md={filterSize > 0 && selectedRows.length > 0 ? 8 : 6}
          sm={12}
          xs={12}
          className="searchBtnWrapper newSearchBtnWrapper"
        >
          <Box className="searchWrapper">
            <OlcSearchInput
              searchApplied={searchApplied}
              sx={{
                '@media (max-width: 900px)': {
                  maxWidth:
                    filterSize > 0 && selectedRows.length > 0 ? '100% !important' : 'inherit',
                },
              }}
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value.trimStart());
              }}
              onKeyDown={debouncedHandleKeyDown}
              placeholder={MESSAGES.ORDERS.SEARCH_PLACE_HOLDER}
              title={MESSAGES.ORDERS.SEARCH_PLACE_HOLDER}
              onClear={() => {
                setSearch('');
                setSearchApplied(false);
              }}
            />
            <Box className="filterBtnWrapper">
              <OlcButton
                variant="secondary"
                size="small"
                onClick={() => {
                  setOpenAdvanceFilters(true);
                }}
              >
                Filters
              </OlcButton>
              {filterSize > 0 && <Typography className="filtersCounter">{filterSize}</Typography>}
            </Box>
            {filterSize > 0 && (
              <OlcButton
                variant="secondary"
                onClick={() => {
                  childRef.current.clearFilters();
                }}
                className="clearFilterBtn"
              >
                {MESSAGES.ORDERS.CLEAR_FILTER_BTN_TEXT}
              </OlcButton>
            )}
          </Box>
        </Grid>
        <Grid
          item
          lg={4}
          md={filterSize > 0 && selectedRows.length > 0 ? 4 : 6}
          sm={12}
          xs={12}
          className="ordersActionsGrid"
          sx={{
            alignItems: 'flex-start',
          }}
        >
          {/* Create Button */}
          <Box className="actionsBtnWrapperOrders">
            {selectedRows.length > 0 && (
              <OlcButton
                variant="secondary"
                size="small"
                dropdown
                expanded={open}
                onClick={handleClick}
              >
                Actions
              </OlcButton>
            )}
            <OlcButton variant="primary" size="small" onClick={handleCreateOrder}>
              Create
            </OlcButton>
          </Box>
        </Grid>
      </Grid>
      <Table
        pagination={pagination}
        updatePagination={setPagination}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        searchApplied={searchApplied}
        setRescheduleModalOpen={setRescheduleModalOpen}
        setSelectedOrder={setSelectedOrder}
        setActiveRow={setActiveRow}
        getOrders={getOrders}
        totalOrdersInDB={totalOrdersInDB}
        filterSize={filterSize}
      />
      <AdvanceFilters
        open={openAdvanceFilters}
        handleCloseFilter={() => {
          setOpenAdvanceFilters(false);
        }}
        ref={childRef}
        updatePagination={setPagination}
      />

      {/* Confirm Modal Generic */}
      <ConfirmModal openConfirmModal={openConfirmModal} setOpenConfirmModal={setOpenConfirmModal} />

      <CreateTestOrderConfirmModal
        open={openCreateConfirm}
        onClose={() => setOpenCreateConfirm(false)}
        onConfirm={confirmCreateOrder}
      />

      <RescheduleOrderModal
        openModal={rescheduleModalOpen}
        handleCloseModal={() => setRescheduleModalOpen(false)}
        selectedOrder={selectedOrder}
        getOrders={getOrders}
        activeRow={activeRow}
      />
      <OlcActionMenu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{
          marginTop: '4px',
        }}
      >
        <MenuItem onClick={() => handleActions('payAndProcess')}>
          <img src={PayAndProcessNewCIcon} alt="pay and process" />
          Pay and Process
        </MenuItem>
        <MenuItem onClick={() => handleActions('cancel')}>
          <img src={CancelOrderNewCIcon} alt="cancel" />
          Cancel
        </MenuItem>
        <MenuItem onClick={() => handleActions('export')}>
          <img src={ExportNewCIcon} alt="export" />
          Export Orders
        </MenuItem>
      </OlcActionMenu>
    </Container>
  );
};

export default Orders;
